import {
  getFallbackHandlerContractDeployment,
  getMultiSendCallOnlyContractDeployment,
  getProxyFactoryContractDeployment,
  getSafeContractDeployment,
  getSignMessageLibContractDeployment,
} from './deployments'
import { LATEST_SAFE_VERSION } from '@/config/constants'
import { ImplementationVersionState } from '@safe-global/safe-gateway-typescript-sdk'
import type { ChainInfo, SafeInfo } from '@safe-global/safe-gateway-typescript-sdk'
import type { GetContractProps, SafeVersion } from '@safe-global/safe-core-sdk-types'
import { assertValidSafeVersion, createEthersAdapter, createReadOnlyEthersAdapter } from '@/hooks/coreSDK/safeCoreSDK'
import type { BrowserProvider } from 'ethers'
import type { EthersAdapter, SafeContractEthers, SignMessageLibEthersContract } from '@safe-global/protocol-kit'
import semver from 'semver'

import type CompatibilityFallbackHandlerEthersContract from '@safe-global/protocol-kit/dist/src/adapters/ethers/contracts/CompatibilityFallbackHandler/CompatibilityFallbackHandlerEthersContract'

// Custom contract addresses for chains not in @safe-global/safe-deployments
export const CUSTOM_CONTRACT_NETWORKS: Record<
  string,
  {
    safeSingletonAddress: string
    safeProxyFactoryAddress: string
    fallbackHandlerAddress: string
    multiSendAddress: string
    multiSendCallOnlyAddress: string
    signMessageLibAddress: string
    createCallAddress: string
    simulateTxAccessorAddress: string
  }
> = {
  '777178': {
    safeSingletonAddress: '0xa4B55B42d0F7f610d8d0fdB13d36f26e2e5e5E3C',
    safeProxyFactoryAddress: '0x6c8C5A08B91cf1223106855D761340a2189E39FD',
    fallbackHandlerAddress: '0xB1f7a6314BDf71d238aCd56C64798f72546d6647',
    multiSendAddress: '0x0C9B2491a0d9DE3736bB8458e8C23BB205Db6C42',
    multiSendCallOnlyAddress: '0x1819F011094a542CBed3749a30Ea5BA56d2Ad13e',
    signMessageLibAddress: '0x68713890D6162859Cb961aE9c85Fd57427736324',
    createCallAddress: '0x948c25820fc88cC29c7C86f7B420C9e0761d1e0A',
    simulateTxAccessorAddress: '0x6A49A1Ea94e23cfb8500b07d858A2bA301B0B5e2',
  },
  '1': {
    safeSingletonAddress: '0xa4B55B42d0F7f610d8d0fdB13d36f26e2e5e5E3C',
    safeProxyFactoryAddress: '0x6c8C5A08B91cf1223106855D761340a2189E39FD',
    fallbackHandlerAddress: '0xB1f7a6314BDf71d238aCd56C64798f72546d6647',
    multiSendAddress: '0x0C9B2491a0d9DE3736bB8458e8C23BB205Db6C42',
    multiSendCallOnlyAddress: '0x1819F011094a542CBed3749a30Ea5BA56d2Ad13e',
    signMessageLibAddress: '0x68713890D6162859Cb961aE9c85Fd57427736324',
    createCallAddress: '0x948c25820fc88cC29c7C86f7B420C9e0761d1e0A',
    simulateTxAccessorAddress: '0x6A49A1Ea94e23cfb8500b07d858A2bA301B0B5e2',
  },
}

// `UNKNOWN` is returned if the mastercopy does not match supported ones
// @see https://github.com/safe-global/safe-client-gateway/blob/main/src/routes/safes/handlers/safes.rs#L28-L31
//      https://github.com/safe-global/safe-client-gateway/blob/main/src/routes/safes/converters.rs#L77-L79
export const isValidMasterCopy = (implementationVersionState: SafeInfo['implementationVersionState']): boolean => {
  return implementationVersionState !== ImplementationVersionState.UNKNOWN
}

export const _getValidatedGetContractProps = (
  safeVersion: SafeInfo['version'],
): Pick<GetContractProps, 'safeVersion'> => {
  assertValidSafeVersion(safeVersion)

  // SDK request here: https://github.com/safe-global/safe-core-sdk/issues/261
  // Remove '+L2'/'+Circles' metadata from version
  const [noMetadataVersion] = safeVersion.split('+')

  return {
    safeVersion: noMetadataVersion as SafeVersion,
  }
}

// GnosisSafe

const getGnosisSafeContractEthers = async (safe: SafeInfo, ethAdapter: EthersAdapter): Promise<SafeContractEthers> => {
  return ethAdapter.getSafeContract({
    customContractAddress: safe.address.value,
    ..._getValidatedGetContractProps(safe.version),
  })
}

export const getReadOnlyCurrentGnosisSafeContract = async (safe: SafeInfo): Promise<SafeContractEthers> => {
  const ethAdapter = createReadOnlyEthersAdapter()
  return getGnosisSafeContractEthers(safe, ethAdapter)
}

export const getCurrentGnosisSafeContract = async (
  safe: SafeInfo,
  provider: BrowserProvider,
): Promise<SafeContractEthers> => {
  const ethAdapter = await createEthersAdapter(provider)
  return getGnosisSafeContractEthers(safe, ethAdapter)
}

export const getReadOnlyGnosisSafeContract = async (chain: ChainInfo, safeVersion: string = LATEST_SAFE_VERSION) => {
  const ethAdapter = createReadOnlyEthersAdapter()
  const customContractAddress = CUSTOM_CONTRACT_NETWORKS[chain.chainId]?.safeSingletonAddress
  const singletonDeployment = customContractAddress ? undefined : getSafeContractDeployment(chain, safeVersion)

  return ethAdapter.getSafeContract({
    singletonDeployment,
    customContractAddress,
    ..._getValidatedGetContractProps(safeVersion),
  })
}

// MultiSend

export const _getMinimumMultiSendCallOnlyVersion = (safeVersion: SafeInfo['version']) => {
  const INITIAL_CALL_ONLY_VERSION = '1.3.0'

  if (!safeVersion) {
    return INITIAL_CALL_ONLY_VERSION
  }

  return semver.gte(safeVersion, INITIAL_CALL_ONLY_VERSION) ? safeVersion : INITIAL_CALL_ONLY_VERSION
}

export const getMultiSendCallOnlyContract = async (
  chainId: string,
  safeVersion: SafeInfo['version'],
  provider: BrowserProvider,
) => {
  const ethAdapter = await createEthersAdapter(provider)
  const multiSendVersion = _getMinimumMultiSendCallOnlyVersion(safeVersion)

  return ethAdapter.getMultiSendCallOnlyContract({
    singletonDeployment: getMultiSendCallOnlyContractDeployment(chainId, multiSendVersion),
    ..._getValidatedGetContractProps(safeVersion),
  })
}

export const getReadOnlyMultiSendCallOnlyContract = async (chainId: string, safeVersion: SafeInfo['version']) => {
  const ethAdapter = createReadOnlyEthersAdapter()
  const multiSendVersion = _getMinimumMultiSendCallOnlyVersion(safeVersion)

  return ethAdapter.getMultiSendCallOnlyContract({
    singletonDeployment: getMultiSendCallOnlyContractDeployment(chainId, multiSendVersion),
    ..._getValidatedGetContractProps(safeVersion),
  })
}

// GnosisSafeProxyFactory

export const getReadOnlyProxyFactoryContract = (chainId: string, safeVersion: SafeInfo['version']) => {
  const ethAdapter = createReadOnlyEthersAdapter()
  const customContractAddress = CUSTOM_CONTRACT_NETWORKS[chainId]?.safeProxyFactoryAddress
  const singletonDeployment = customContractAddress
    ? undefined
    : getProxyFactoryContractDeployment(chainId, safeVersion)

  return ethAdapter.getSafeProxyFactoryContract({
    singletonDeployment,
    customContractAddress,
    ..._getValidatedGetContractProps(safeVersion),
  })
}

// Fallback handler

export const getReadOnlyFallbackHandlerContract = async (
  chainId: string,
  safeVersion: SafeInfo['version'],
): Promise<CompatibilityFallbackHandlerEthersContract> => {
  const ethAdapter = createReadOnlyEthersAdapter()
  const customContractAddress = CUSTOM_CONTRACT_NETWORKS[chainId]?.fallbackHandlerAddress
  const singletonDeployment = customContractAddress
    ? undefined
    : getFallbackHandlerContractDeployment(chainId, safeVersion)

  return ethAdapter.getCompatibilityFallbackHandlerContract({
    singletonDeployment,
    customContractAddress,
    ..._getValidatedGetContractProps(safeVersion),
  })
}

// Sign messages deployment

export const getReadOnlySignMessageLibContract = async (
  chainId: string,
  safeVersion: SafeInfo['version'],
): Promise<SignMessageLibEthersContract> => {
  const ethAdapter = createReadOnlyEthersAdapter()

  return ethAdapter.getSignMessageLibContract({
    singletonDeployment: getSignMessageLibContractDeployment(chainId, safeVersion),
    ..._getValidatedGetContractProps(safeVersion),
  })
}
